import { formatEra } from "./utils";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, BarChart2 } from "lucide-react";

// Pitcher Substitution Dialog
export function PitcherSubDialog({ open, onClose, pitchers, currentPitcher, onSelect, onViewStats }) {
  const available = pitchers.filter(p => !p.subbed_out && p.id !== currentPitcher?.id);
  const used = pitchers.filter(p => p.subbed_out);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xs bg-slate-900 border-slate-700 p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-slate-800">
          <DialogTitle className="text-sm font-black flex items-center gap-2 text-white tracking-wide">
            <RefreshCw size={14} className="text-amber-400" /> Bullpen
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* Current pitcher */}
          {currentPitcher && (
            <div>
              <p className="text-[9px] font-black tracking-widest text-emerald-400 uppercase mb-1.5">On Mound</p>
              <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="w-7 h-7 rounded-md bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-black text-[11px]">P</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-white truncate">{currentPitcher.name}</p>
                  <p className="text-[10px] text-slate-500">{currentPitcher.pitch_chips || 4} chips</p>
                </div>
                {onViewStats && (
                  <button onClick={() => onViewStats(currentPitcher)} className="shrink-0 p-1 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700/60 transition-colors">
                    <BarChart2 size={14} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Available relievers */}
          {available.length > 0 && (
            <div>
              <p className="text-[9px] font-black tracking-widest text-amber-400 uppercase mb-1.5">Available</p>
              <div className="space-y-1.5">
                {available.map((pitcher) => (
                  <div
                    key={pitcher.id}
                    onClick={() => onSelect(pitcher)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 hover:border-amber-500/50 hover:bg-slate-800/80 transition-all cursor-pointer"
                  >
                    <div className="w-7 h-7 rounded-md bg-slate-700 border border-slate-600 flex items-center justify-center text-slate-400 font-black text-[11px]">P</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-white truncate">{pitcher.name}</p>
                      <p className="text-[10px] text-slate-500">{pitcher.pitch_chips || 4} chips</p>
                    </div>
                    {onViewStats && (
                      <button onClick={(e) => { e.stopPropagation(); onViewStats(pitcher); }} className="shrink-0 p-1 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700/60 transition-colors">
                        <BarChart2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {available.length === 0 && !used.length && (
            <p className="text-xs text-slate-500 text-center py-2">No relievers available</p>
          )}

          {/* Used / out of game */}
          {used.length > 0 && (
            <div>
              <p className="text-[9px] font-black tracking-widest text-slate-600 uppercase mb-1.5">Out of Game</p>
              <div className="space-y-1.5">
                {used.map((pitcher) => (
                  <div
                    key={pitcher.id}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-800/40 border border-slate-700/40 opacity-40 cursor-not-allowed"
                  >
                    <div className="w-7 h-7 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-600 font-black text-[11px]">P</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-slate-500 truncate line-through">{pitcher.name}</p>
                      <p className="text-[10px] text-slate-600">Cannot re-enter</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Batter / Fielder Substitution Dialog
export function BatterSubDialog({ open, onClose, playerOut, benchPlayers, onSubstitute, onViewStats, title = "Substitute Batter" }) {
  if (!playerOut) return null;
  const eligible = (benchPlayers || []).filter(p => !p.subbed_out);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xs bg-slate-900 border-slate-700 p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-slate-800">
          <DialogTitle className="text-sm font-black flex items-center gap-2 text-white tracking-wide">
            <RefreshCw size={14} className="text-amber-400" /> {title}
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 space-y-3">
          {/* Player being replaced */}
          <div>
            <p className="text-[9px] font-black tracking-widest text-red-400 uppercase mb-1.5">Subbing Out</p>
            <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-red-500/8 border border-red-500/25">
              <span className="text-[11px] font-black font-mono bg-slate-800 px-1.5 py-0.5 rounded text-slate-300 border border-slate-700">{playerOut.position}</span>
              <span className="font-bold text-sm text-white truncate flex-1">{playerOut.name}</span>
              {onViewStats && (
                <button onClick={() => onViewStats(playerOut)} className="shrink-0 p-1 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700/60 transition-colors">
                  <BarChart2 size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Bench options */}
          <div>
            <p className="text-[9px] font-black tracking-widest text-amber-400 uppercase mb-1.5">Select Replacement</p>
            {eligible.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-3">No bench players available</p>
            ) : (
              <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                {eligible.map((player) => (
                  <div
                    key={player.id}
                    onClick={() => onSubstitute(player)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 hover:border-amber-500/50 hover:bg-slate-800/80 transition-all cursor-pointer"
                  >
                    <span className="text-[11px] font-black font-mono bg-slate-700 px-1.5 py-0.5 rounded text-slate-300 border border-slate-600">{player.position}</span>
                    <span className="font-bold text-sm text-white truncate flex-1">{player.name}</span>
                    {onViewStats && (
                      <button onClick={(e) => { e.stopPropagation(); onViewStats(player); }} className="shrink-0 p-1 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-700/60 transition-colors">
                        <BarChart2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Player Game Stats Card
export function PlayerDetailsDialog({ open, onClose, player, gameStats }) {
  // Auto-scroll the highlighted card-year row into view when the dialog opens
  // for a player with a long career — without this the user can land on row 1
  // of 25 and miss the ★ row entirely. Runs after the open transition so the
  // table is in the DOM when querySelector runs.
  const playerId = player?.id;
  useEffect(() => {
    if (!open || !playerId) return;
    const t = setTimeout(() => {
      try {
        document.querySelector('[data-card-year="true"]')?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [open, playerId]);
  if (!player) return null;

  const isPitcher = player.position === "P";
  const ps = gameStats?.players?.[player.id] || {};

  // Career season rows: take player.seasons and mark the card-year row with the
  // ★ highlight. Only append a synthetic card-year row if it's NOT already in the
  // seasons list (legacy data without a seasons array). Without the dedup we'd
  // render the same year twice when the trim window matches the card year — which
  // is always the case for the new trim path (player.year = last trimmed season).
  const cardYear = player.year;
  const cardStats = player.stats || {};
  const baseSeasons = (player.seasons || []).map(s => ({
    ...s,
    isCardYear: s.year === cardYear,
  }));
  const hasCardYearRow = cardYear != null && baseSeasons.some(s => s.year === cardYear);
  const allSeasons = [
    ...baseSeasons,
    ...(!hasCardYearRow && cardYear && (cardStats.home_runs > 0 || cardStats.strikeouts > 0 || cardStats.era !== 4.00)
      ? [{ year: cardYear, stats: cardStats, team: player.team, isCardYear: true }]
      : []),
  ].sort((a, b) => a.year - b.year);

  function fmtAvg(v) {
    if (v == null) return '—';
    return v.toFixed(3).replace(/^0/, '');
  }
  function fmtSlg(v) {
    if (v == null) return '—';
    return v.toFixed(3).replace(/^0/, '');
  }
  function fmtEra(v) {
    if (v == null) return '—';
    return v.toFixed(2);
  }
  function fmtInt(v) {
    return (v == null || v === undefined) ? '—' : v;
  }
  function fmtIP(v) {
    if (v == null) return '—';
    // MLB convention: .0 / .1 / .2 thirds — keep as-is with one decimal
    return Number(v).toFixed(1);
  }

  // Derived pitcher stats
  const k = ps.k || 0;
  const bb = ps.bb || 0;
  const h = ps.h || 0;
  const outs = ps.outs || 0;
  const runsAllowed = ps.runsAllowed || 0;
  const era = formatEra(runsAllowed, outs);
  const chipsUsed = ps.chipsUsed || 0;
  const mgrUsed = ps.mgrUsed || 0;
  const chipsLeft = isPitcher ? Math.max(0, (player.pitch_chips || 4) - chipsUsed) : null;

  // Derived batter stats
  const ab = ps.ab || 0;
  const hits = ps.h || 0;
  const avg = ab > 0 ? (hits / ab).toFixed(3).replace('0.', '.') : '—';

  function Stat({ label, value, accent }) {
    const colors = {
      amber: 'text-amber-400',
      red: 'text-red-400',
      emerald: 'text-emerald-400',
      blue: 'text-blue-400',
      white: 'text-white',
    };
    return (
      <div className="bg-slate-800/60 rounded-lg p-3 text-center">
        <div className="text-[9px] text-slate-500 font-bold tracking-wider uppercase mb-1">{label}</div>
        <div className={`font-mono font-black text-xl ${colors[accent] || 'text-white'}`}>{value ?? '—'}</div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700/60">
        <DialogHeader>
          <DialogTitle className="text-sm font-black text-slate-400 tracking-wider uppercase">Player Card</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pb-1">
          {/* Player info badges */}
          <div className="bg-slate-800/50 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded-lg text-sm font-black border ${isPitcher ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'bg-amber-500/15 border-amber-500/30 text-amber-400'}`}>
                {player.position}
              </span>
              <span className="font-black text-white text-base leading-tight">{player.name}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {player.bats && (
                <span className="px-2 py-0.5 rounded-md bg-slate-700/60 border border-slate-600/50 text-[10px] font-bold text-slate-300">
                  Bats <span className="text-white">{player.bats}</span>
                </span>
              )}
              {isPitcher && (
                <span className="px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-[10px] font-bold text-amber-400">
                  {player.pitch_chips || 4} chips
                </span>
              )}
              {isPitcher && chipsLeft !== null && (
                <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold ${chipsLeft <= 1 ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-slate-700/60 border-slate-600/50 text-slate-300'}`}>
                  {chipsLeft} left
                </span>
              )}
            </div>
          </div>

          {/* Stats table */}
          {isPitcher ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">SO</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">BB</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">H</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">OUTS</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">RA</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">ERA</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-1.5 font-mono font-black text-red-400">{k}</td>
                  <td className="py-1.5 font-mono font-black text-blue-400">{bb}</td>
                  <td className="py-1.5 font-mono font-black text-amber-400">{h}</td>
                  <td className="py-1.5 font-mono font-black text-white">{outs}</td>
                  <td className="py-1.5 font-mono font-black text-red-400">{runsAllowed}</td>
                  <td className="py-1.5 font-mono font-black text-white">{era}</td>
                </tr>
              </tbody>
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th colSpan={3} className="text-left text-[9px] text-slate-500 font-bold pb-1.5 pt-2 tracking-wider">CHIPS USED</th>
                  <th colSpan={3} className="text-left text-[9px] text-slate-500 font-bold pb-1.5 pt-2 tracking-wider">MGR USED</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={3} className="py-1.5 font-mono font-black text-amber-400">{chipsUsed}</td>
                  <td colSpan={3} className="py-1.5 font-mono font-black text-white">{mgrUsed}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">AB</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">H</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">AVG</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">SO</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">BB</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">HR</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">RBI</th>
                  <th className="text-left text-[9px] text-slate-500 font-bold pb-1.5 tracking-wider">SB</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-1.5 font-mono font-black text-white">{ab}</td>
                  <td className="py-1.5 font-mono font-black text-emerald-400">{hits}</td>
                  <td className="py-1.5 font-mono font-black text-emerald-400">{avg}</td>
                  <td className="py-1.5 font-mono font-black text-red-400">{k}</td>
                  <td className="py-1.5 font-mono font-black text-blue-400">{bb}</td>
                  <td className="py-1.5 font-mono font-black text-amber-400">{ps.hr || 0}</td>
                  <td className="py-1.5 font-mono font-black text-amber-400">{ps.rbi || 0}</td>
                  <td className="py-1.5 font-mono font-black text-emerald-300">{ps.sb || 0}</td>
                </tr>
              </tbody>
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th colSpan={2} className="text-left text-[9px] text-slate-500 font-bold pb-1.5 pt-2 tracking-wider">2B</th>
                  <th colSpan={2} className="text-left text-[9px] text-slate-500 font-bold pb-1.5 pt-2 tracking-wider">CHIPS</th>
                  <th colSpan={4} className="text-left text-[9px] text-slate-500 font-bold pb-1.5 pt-2 tracking-wider">MGR</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={2} className="py-1.5 font-mono font-black text-white">{ps.doubles || 0}</td>
                  <td colSpan={2} className="py-1.5 font-mono font-black text-amber-400">{chipsUsed}</td>
                  <td colSpan={4} className="py-1.5 font-mono font-black text-white">{mgrUsed}</td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Career / Card Stats — 13 stat columns per role to match the full backing
              schema (see PlayerStats in backend/models.py). Horizontal scroll on narrow
              widths so the table doesn't get clipped when the dialog is xs. */}
          {allSeasons.length > 0 && (
            <div>
              <p className="text-[9px] text-slate-500 font-bold tracking-wider uppercase mb-1.5">Card Stats</p>
              <div className="bg-slate-800/40 rounded-lg max-h-52 overflow-y-auto overflow-x-auto">
                {isPitcher ? (
                  // Pitcher columns (in user-specified order):
                  // G, IP, W, L, R, ER, SO, BB, GS, CG, SHO, SV, ERA
                  <table className="text-[10px] min-w-full whitespace-nowrap">
                    <thead className="sticky top-0 bg-slate-800/90">
                      <tr className="border-b border-slate-700/50">
                        <th className="text-left px-1 py-1.5 text-[9px] text-slate-600 font-bold">YR</th>
                        <th className="text-left px-1 py-1.5 text-[9px] text-slate-600 font-bold">TM</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">G</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">IP</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">W</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">L</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">R</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">ER</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">SO</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">BB</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">GS</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">CG</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">SHO</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">SV</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">ERA</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSeasons.map(s => {
                        const cellCls = `px-1 py-1 font-mono text-right ${s.isCardYear ? 'text-amber-300 font-bold' : 'text-slate-400'}`;
                        return (
                          <tr key={s.year} data-card-year={s.isCardYear ? "true" : undefined} className={`border-t ${s.isCardYear ? 'border-amber-500/30 bg-amber-500/10' : 'border-slate-700/30'}`}>
                            <td className={`px-1 py-1 font-mono font-bold ${s.isCardYear ? 'text-amber-400' : 'text-slate-500'}`}>{s.year}{s.isCardYear ? ' ★' : ''}</td>
                            <td className={`px-1 py-1 font-mono text-left text-[9px] ${s.isCardYear ? 'text-amber-400/70' : 'text-slate-600'}`}>{s.team || '—'}</td>
                            <td className={cellCls}>{fmtInt(s.stats.games_played)}</td>
                            <td className={cellCls}>{fmtIP(s.stats.innings_pitched)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.wins)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.losses)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.runs)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.earned_runs)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.strikeouts)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.walks)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.games_started)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.complete_games)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.shutouts)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.saves)}</td>
                            <td className={cellCls}>{fmtEra(s.stats.era)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  // Fielder columns (in user-specified order):
                  // G, AB, R, H, 2B, 3B, HR, RBI, SB, SLG, BB, SO, AVG
                  <table className="text-[10px] min-w-full whitespace-nowrap">
                    <thead className="sticky top-0 bg-slate-800/90">
                      <tr className="border-b border-slate-700/50">
                        <th className="text-left px-1 py-1.5 text-[9px] text-slate-600 font-bold">YR</th>
                        <th className="text-left px-1 py-1.5 text-[9px] text-slate-600 font-bold">TM</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">G</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">AB</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">R</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">H</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">2B</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">3B</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">HR</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">RBI</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">SB</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">SLG</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">BB</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">SO</th>
                        <th className="text-right px-1 py-1.5 text-[9px] text-slate-600 font-bold">AVG</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSeasons.map(s => {
                        const cellCls = `px-1 py-1 font-mono text-right ${s.isCardYear ? 'text-amber-300 font-bold' : 'text-slate-400'}`;
                        return (
                          <tr key={s.year} data-card-year={s.isCardYear ? "true" : undefined} className={`border-t ${s.isCardYear ? 'border-amber-500/30 bg-amber-500/10' : 'border-slate-700/30'}`}>
                            <td className={`px-1 py-1 font-mono font-bold ${s.isCardYear ? 'text-amber-400' : 'text-slate-500'}`}>{s.year}{s.isCardYear ? ' ★' : ''}</td>
                            <td className={`px-1 py-1 font-mono text-left text-[9px] ${s.isCardYear ? 'text-amber-400/70' : 'text-slate-600'}`}>{s.team || '—'}</td>
                            <td className={cellCls}>{fmtInt(s.stats.games_played)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.at_bats)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.runs)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.hits)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.doubles)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.triples)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.home_runs)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.rbi)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.stolen_bases)}</td>
                            <td className={cellCls}>{fmtSlg(s.stats.slugging)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.walks)}</td>
                            <td className={cellCls}>{fmtInt(s.stats.strikeouts)}</td>
                            <td className={cellCls}>{fmtAvg(s.stats.batting_average)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
